import type { Map as MapLibreMap } from 'maplibre-gl'

import type { WmsTarget } from './wms'

/**
 * WMS `GetFeatureInfo`, which answers two different questions here.
 *
 * On the alerts layer it is the only interaction mechanism nowCOAST offers
 * (its WFS publishes bathymetry, not alerts), and the response carries the CAP
 * id — all the backend needs to fetch the full NWS alert.
 *
 * On the raster layers it returns the *value* under the pixel, as `Band1`, in
 * the layer's own units: 25 dBZ of reflectivity, 72.4 °F of NDFD temperature,
 * 15 % probability of precipitation. Probed 2026-09-17. It honours `TIME`, so
 * the number reported matches the frame on screen rather than the latest one.
 *
 * The pixel arithmetic is the trap here. `i`/`j` — WMS 1.3.0's spelling of
 * 1.1.1's `x`/`y` — and `width`/`height` must all be in the *same* pixel
 * space, and MapLibre's `e.point` is in CSS pixels. `canvas.width` is device
 * pixels, so on a 3x phone display it is off by a factor of three and every
 * tap resolves to the wrong alert.
 */

export interface AlertFeature {
  /**
   * Null for a product nowCOAST draws with no CAP id behind it. Measured
   * 2026-10-07: NHC's offshore marine zones (`wfo: "NH2"`, a Tropical Storm
   * Warning over the Gulf) arrive with `cap_id: null`, so there is no text
   * to open — but the polygon is on the map, and "None at this point" under
   * it is wrong. Such a row is listed and not tappable.
   */
  capId: string | null
  /** Unique within one response: the CAP id, or the product and its expiry. */
  key: string
  prodType: string
  msgType: string | null
  issuance: string | null
  expiration: string | null
  /**
   * nowCOAST's own drawing priority, which is the only significance signal in
   * the response — the alternative is fetching every alert's full text just to
   * sort the list. Sampled 2026-09-17: Special Weather Statement 10, Fire
   * Weather Watch 15, Flood Watch 22, Flood Advisory 47, Flood Warning 71.
   * Higher is drawn on top, and is more significant.
   */
  renderOrder: number
}

export interface FeatureInfoQuery extends WmsTarget {
  /** EPSG:3857 bbox of the current view: minx,miny,maxx,maxy. */
  bbox: [number, number, number, number]
  /** CSS pixels, matching the space `i`/`j` are measured in. */
  width: number
  height: number
  i: number
  j: number
  featureCount?: number
  /** ISO8601. Raster layers honour it, so a readout matches the frame shown. */
  time?: string
}

const MAX_LAT = 85.051129

/** Spherical Mercator (EPSG:3857) from lon/lat degrees. */
export function toMercator(lon: number, lat: number): [number, number] {
  const R = 6378137
  const clamped = Math.max(-MAX_LAT, Math.min(MAX_LAT, lat))
  const x = (R * lon * Math.PI) / 180
  const y = R * Math.log(Math.tan(Math.PI / 4 + (clamped * Math.PI) / 360))
  return [x, y]
}

export function featureInfoUrl(q: FeatureInfoQuery): string {
  const p = new URLSearchParams({
    service: 'WMS',
    version: '1.3.0',
    request: 'GetFeatureInfo',
    layers: q.layerName,
    query_layers: q.layerName,
    styles: '',
    crs: 'EPSG:3857',
    bbox: q.bbox.join(','),
    width: String(Math.round(q.width)),
    height: String(Math.round(q.height)),
    i: String(Math.round(q.i)),
    j: String(Math.round(q.j)),
    info_format: 'application/json',
    feature_count: String(q.featureCount ?? 5),
  })
  if (q.time) p.set('TIME', q.time)
  return `${q.base}?${p.toString()}`
}

export function parseFeatureInfo(json: unknown): AlertFeature[] {
  const features = (json as { features?: unknown[] })?.features
  if (!Array.isArray(features)) return []
  const out: AlertFeature[] = []
  const seen = new Set<string>()
  for (const f of features) {
    const p = (f as { properties?: Record<string, unknown> })?.properties
    if (!p) continue
    const capId = typeof p.cap_id === 'string' && p.cap_id ? p.cap_id : null
    const prodType = typeof p.prod_type === 'string' ? p.prod_type : null
    const expiration = typeof p.expiration === 'string' ? p.expiration : null
    // A feature with neither an id nor a product name says nothing nameable.
    if (!capId && !prodType) continue
    // Without an id, one product over several adjacent zones is one row.
    const key = capId ?? `${prodType}|${p.wfo ?? ''}|${expiration ?? ''}`
    // The `url` property is ignored on purpose: handing a third party the
    // address our backend fetches is a server-side request forgery.
    if (seen.has(key)) continue
    seen.add(key)
    out.push({
      capId,
      key,
      prodType: prodType ?? 'Alert',
      msgType: typeof p.msg_type === 'string' ? p.msg_type : null,
      issuance: typeof p.issuance === 'string' ? p.issuance : null,
      expiration,
      renderOrder: typeof p.render_order === 'number' ? p.render_order : 0,
    })
  }
  // Most significant first: a tornado warning inside a flood watch should not
  // be the second thing in the list.
  out.sort((a, b) => b.renderOrder - a.renderOrder)
  return out
}

/**
 * The raster value under the queried pixel. GeoServer names the band `Band1`;
 * accept any finite numeric property so a renamed band does not silently read
 * as "no data".
 */
export function parseRasterValue(json: unknown): number | null {
  const props = (json as { features?: { properties?: Record<string, unknown> }[] })?.features?.[0]
    ?.properties
  if (!props) return null
  const raw = props.Band1 ?? Object.values(props).find((v) => typeof v === 'number')
  return typeof raw === 'number' && Number.isFinite(raw) ? raw : null
}

/**
 * Build the query for a click on `map`. `point` is MapLibre's `e.point`, which
 * is already in the CSS-pixel space `clientWidth`/`clientHeight` describe.
 */
export function queryForClick(
  map: MapLibreMap,
  target: WmsTarget,
  point: { x: number; y: number },
  time?: Date | null,
): FeatureInfoQuery {
  const bounds = map.getBounds()
  const [minx, miny] = toMercator(bounds.getWest(), bounds.getSouth())
  const [maxx, maxy] = toMercator(bounds.getEast(), bounds.getNorth())
  const canvas = map.getCanvas()
  return {
    ...target,
    bbox: [minx, miny, maxx, maxy],
    width: canvas.clientWidth,
    height: canvas.clientHeight,
    i: point.x,
    j: point.y,
    time: time ? time.toISOString() : undefined,
  }
}

export async function queryFeatureInfo(q: FeatureInfoQuery): Promise<AlertFeature[]> {
  const resp = await fetch(featureInfoUrl(q))
  if (!resp.ok) return []
  return parseFeatureInfo(await resp.json())
}
